/**
 * ActividadCliente — Sprint B IMPL-046 (2026-05-22).
 *
 * Vista de actividad por cliente: timeline de actas + sidebar con stats, oferta y tareas.
 * Sin lógica IA aún (placeholder). El procesamiento llega en Sprint C.
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, FileText, Mail, Phone, Briefcase,
  Loader2, Trash2, Check, Square, ChevronDown, ChevronUp,
  RefreshCw, Sparkles, AlertTriangle, TrendingUp, Eye, EyeOff,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authAxios = axios.create();
authAxios.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("mobilsafe_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const EUR = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

const TIPOS = [
  { v: "nota", label: "Nota", icon: FileText },
  { v: "email", label: "Email", icon: Mail },
  { v: "llamada", label: "Llamada", icon: Phone },
  { v: "visita", label: "Visita", icon: Briefcase },
];

const ICONO_TIPO = {
  nota: FileText,
  email: Mail,
  llamada: Phone,
  visita: Briefcase,
};

export default function ActividadCliente({ cliente, onBack }) {
  const [tab, setTab] = useState("actividad");
  const [actas, setActas] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [ofertas, setOfertas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cliente?.id]);

  async function load() {
    if (!cliente?.id) return;
    setLoading(true);
    try {
      const [rA, rT, rO] = await Promise.all([
        authAxios.get(`${API}/actas?cliente_id=${cliente.id}&limit=100`),
        authAxios.get(`${API}/tareas?cliente_id=${cliente.id}&limit=100`),
        authAxios.get(`${API}/ofertas?cliente_id=${cliente.id}&limit=10`),
      ]);
      setActas(rA.data || []);
      setTareas(rT.data || []);
      setOfertas(rO.data || []);
    } catch {
      toast.error("Error cargando actividad");
    }
    setLoading(false);
  }

  const tareasPendientes = tareas.filter(t => t.estado === "pendiente");
  const ofertaMasReciente = ofertas[0] || null;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#0a6640] px-6 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={onBack} data-testid="actividad-back-btn" className="text-white/60 hover:text-white flex items-center gap-1 text-sm flex-shrink-0">
            <ArrowLeft className="w-4 h-4" /> Clientes
          </button>
          <span className="text-white/30">·</span>
          <span className="text-sm font-semibold text-white truncate" data-testid="actividad-cliente-nombre">{cliente.empresa}</span>
          {cliente.cif && <span className="text-xs text-white/50 hidden md:inline">{cliente.cif}</span>}
        </div>
      </header>

      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-6 flex gap-1">
          <Tab id="resumen" current={tab} onClick={setTab}>Resumen</Tab>
          <Tab id="actividad" current={tab} onClick={setTab} testid="tab-actividad">Actividad</Tab>
          <Tab id="ofertas" current={tab} onClick={setTab}>Ofertas</Tab>
          <Tab id="diagnostico" current={tab} onClick={setTab}>Diagnóstico</Tab>
        </div>
      </div>

      <main className="max-w-[1400px] mx-auto px-6 py-6">
        {tab !== "actividad" ? (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-500">
            <p className="font-medium">Próximamente</p>
            <p className="text-sm mt-1">Esta sección se habilita en sprints siguientes.</p>
          </div>
        ) : loading ? (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
            <Sidebar
              cliente={cliente}
              numActas={actas.length}
              tareasPendientes={tareasPendientes}
              ofertaMasReciente={ofertaMasReciente}
              ultimaValoracion={actas.find(a => a.valoracion_ia)?.valoracion_ia}
              onTareaToggle={async (t) => {
                await authAxios.put(`${API}/tareas/${t.id}`, { estado: "completada" });
                toast.success("Tarea completada");
                load();
              }}
            />

            <Timeline
              cliente={cliente}
              actas={actas}
              onReload={load}
            />
          </div>
        )}
      </main>
    </div>
  );
}

function Tab({ id, current, onClick, testid, children }) {
  const active = current === id;
  return (
    <button
      onClick={() => onClick(id)}
      data-testid={testid || `tab-${id}`}
      className={`px-4 py-2.5 text-sm border-b-2 transition whitespace-nowrap ${
        active ? "border-[#0a6640] text-[#0a6640] font-medium" : "border-transparent text-slate-500 hover:text-slate-700"
      }`}
    >
      {children}
    </button>
  );
}

// ============== SIDEBAR ==============

function Sidebar({ cliente, numActas, tareasPendientes, ofertaMasReciente, ultimaValoracion, onTareaToggle }) {
  const diasPipeline = cliente.fecha_creacion
    ? Math.max(0, Math.floor((Date.now() - new Date(cliente.fecha_creacion).getTime()) / 86400000))
    : 0;
  const sem = ultimaValoracion?.semaforo;
  const dealColor = sem === "verde" ? "bg-emerald-500" : sem === "ambar" ? "bg-amber-500" : sem === "rojo" ? "bg-rose-500" : "bg-slate-300";
  const dealLabel = sem === "verde" ? "Avanza bien" : sem === "ambar" ? "En riesgo" : sem === "rojo" ? "Alerta" : "Sin valoración aún";

  return (
    <aside className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 gap-2">
        <StatChip label="Actas" value={numActas} color="text-emerald-700 bg-emerald-50" />
        <StatChip label="Tareas" value={tareasPendientes.length} color="text-amber-700 bg-amber-50" />
        <StatChip label="Días" value={diasPipeline} color="text-slate-700 bg-slate-100" />
        <StatChip label="Estado" value={cliente.estado || "—"} color="text-blue-700 bg-blue-50" small />
      </div>

      {/* Oferta vinculada */}
      <div className="bg-white border border-slate-200 rounded-lg p-3">
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">Oferta vinculada</p>
        {ofertaMasReciente ? (
          <div data-testid="sidebar-oferta">
            <p className="text-sm font-semibold text-slate-800 truncate">{ofertaMasReciente.numero_oferta || ofertaMasReciente.id?.slice(0, 8)}</p>
            <p className="text-xs text-slate-500">{ofertaMasReciente.estado || "—"}</p>
            <p className="text-sm font-mono text-emerald-700 mt-1">{EUR(ofertaMasReciente.importe_total || ofertaMasReciente.importe || 0)}</p>
          </div>
        ) : (
          <p className="text-xs text-slate-400 italic">Sin oferta</p>
        )}
      </div>

      {/* Tareas pendientes */}
      <div className="bg-white border border-slate-200 rounded-lg p-3">
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">Tareas pendientes</p>
        {tareasPendientes.length === 0 ? (
          <p className="text-xs text-slate-400 italic">Sin tareas pendientes</p>
        ) : (
          <ul className="space-y-1.5" data-testid="sidebar-tareas">
            {tareasPendientes.slice(0, 5).map((t) => (
              <li key={t.id} className="flex items-start gap-2 text-xs">
                <button
                  onClick={() => onTareaToggle(t)}
                  data-testid={`sidebar-tarea-toggle-${t.id}`}
                  className="mt-0.5 text-slate-400 hover:text-emerald-600 flex-shrink-0"
                >
                  <Square className="w-3.5 h-3.5" />
                </button>
                <span className="text-slate-700 leading-snug">{t.texto}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Estado deal (toma del último semáforo IA) */}
      <div className="bg-white border border-slate-200 rounded-lg p-3">
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">Estado del deal</p>
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full ${dealColor}`} />
          <p className={`text-xs ${sem ? "text-slate-700 font-medium" : "text-slate-400 italic"}`}>{dealLabel}</p>
        </div>
        {ultimaValoracion?.probabilidad_cierre != null && sem && (
          <p className="text-[10px] text-slate-500 mt-1">Probabilidad cierre: <strong className="text-slate-700">{ultimaValoracion.probabilidad_cierre}%</strong></p>
        )}
      </div>
    </aside>
  );
}

function StatChip({ label, value, color, small }) {
  return (
    <div className={`${color} rounded-lg p-2.5 text-center`}>
      <p className="text-[10px] uppercase tracking-wider opacity-70 font-bold">{label}</p>
      <p className={`font-bold ${small ? "text-sm" : "text-xl"}`}>{value}</p>
    </div>
  );
}

// ============== TIMELINE ==============

function Timeline({ cliente, actas, onReload }) {
  return (
    <div className="space-y-4">
      <NuevaActa cliente={cliente} onSaved={onReload} />
      {actas.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-lg p-8 text-center text-slate-500">
          <FileText className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          <p className="text-sm">Aún no hay actividad registrada</p>
          <p className="text-xs mt-1">Crea la primera nota arriba</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="timeline-actas">
          {actas.map((acta) => (
            <ActaItem key={acta.id} acta={acta} onChange={onReload} />
          ))}
        </div>
      )}
    </div>
  );
}

function NuevaActa({ cliente, onSaved }) {
  const [tipo, setTipo] = useState("nota");
  const [contenido, setContenido] = useState("");
  const [saving, setSaving] = useState(false);

  const guardar = async () => {
    if (!contenido.trim()) {
      toast.error("Escribe el contenido del acta");
      return;
    }
    setSaving(true);
    try {
      await authAxios.post(`${API}/actas`, {
        cliente_id: cliente.id,
        tipo,
        contenido_original: contenido,
        origen: "manual",
      });
      toast.success("Acta guardada · procesando con IA…");
      setContenido("");
      setTipo("nota");
      // Doble recarga: una inmediata + otra a los 3s cuando la IA ha terminado
      onSaved();
      setTimeout(() => { onSaved(); }, 3500);
    } catch (e) {
      toast.error("Error guardando acta");
    }
    setSaving(false);
  };

  return (
    <div className="bg-white border border-[#0a6640] rounded-lg p-4" data-testid="nueva-acta">
      <div className="flex gap-1.5 mb-3 flex-wrap">
        {TIPOS.map((t) => {
          const Icon = t.icon;
          const active = tipo === t.v;
          return (
            <button
              key={t.v}
              onClick={() => setTipo(t.v)}
              data-testid={`nueva-acta-tipo-${t.v}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition ${
                active ? "bg-[#0a6640] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>
      <textarea
        value={contenido}
        onChange={(e) => setContenido(e.target.value)}
        placeholder="Escribe las notas. La IA generará automáticamente título, resumen, valoración y tareas al guardar."
        rows={3}
        data-testid="nueva-acta-textarea"
        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-y"
      />
      <div className="flex justify-between items-center mt-2 gap-3">
        <span className="text-xs text-emerald-700 italic flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" /> IA · resumen + valoración + tareas automáticas
        </span>
        <button
          onClick={guardar}
          disabled={saving || !contenido.trim()}
          data-testid="nueva-acta-guardar"
          className="px-4 py-1.5 bg-[#0a6640] text-white text-sm font-medium rounded-lg hover:bg-[#084d30] disabled:opacity-50 flex items-center gap-1.5"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          {saving ? "Procesando…" : "Guardar acta"}
        </button>
      </div>
    </div>
  );
}

function ActaItem({ acta, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const [subTab, setSubTab] = useState("acta");
  const [notas, setNotas] = useState(acta.notas_comercial || "");
  const [savingNotas, setSavingNotas] = useState(false);
  const [tareasActa, setTareasActa] = useState([]);
  const [showOriginal, setShowOriginal] = useState(false);
  const [reprocesando, setReprocesando] = useState(false);

  const Icon = ICONO_TIPO[acta.tipo] || FileText;
  const preview = acta.titulo || (acta.resumen_ia || acta.contenido_original || "").slice(0, 80);
  const fecha = acta.fecha_actividad ? new Date(acta.fecha_actividad).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  const semaforo = acta.valoracion_ia?.semaforo || null;
  const semaforoColor = {
    verde: "bg-emerald-500",
    ambar: "bg-amber-500",
    rojo: "bg-rose-500",
  }[semaforo] || "bg-slate-300";

  useEffect(() => {
    if (expanded && (acta.tareas || []).length > 0) {
      authAxios.get(`${API}/tareas?cliente_id=${acta.cliente_id}&limit=100`)
        .then(r => setTareasActa((r.data || []).filter(t => acta.tareas?.includes(t.id))))
        .catch(() => setTareasActa([]));
    }
  }, [expanded, acta]);

  const guardarNotas = async () => {
    setSavingNotas(true);
    try {
      await authAxios.put(`${API}/actas/${acta.id}`, { notas_comercial: notas });
      toast.success("Notas guardadas");
    } catch {
      toast.error("Error guardando notas");
    }
    setSavingNotas(false);
  };

  const reprocesar = async () => {
    setReprocesando(true);
    try {
      await authAxios.post(`${API}/actas/${acta.id}/reprocesar`);
      toast.success("Reprocesando con IA…");
      setTimeout(() => { onChange(); setReprocesando(false); }, 4000);
    } catch {
      toast.error("Error reprocesando");
      setReprocesando(false);
    }
  };

  const eliminar = async () => {
    if (!window.confirm("¿Eliminar este acta?")) return;
    try {
      await authAxios.delete(`${API}/actas/${acta.id}`);
      toast.success("Acta eliminada");
      onChange();
    } catch {
      toast.error("Error eliminando");
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`acta-${acta.id}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left"
      >
        <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-800 truncate">{preview || "(sin contenido)"}</p>
          <p className="text-xs text-slate-500 flex items-center gap-1.5">
            {fecha}
            {!acta.ia_procesada && <span className="text-amber-600 italic flex items-center gap-0.5"><Loader2 className="w-3 h-3 animate-spin" /> IA procesando…</span>}
          </p>
        </div>
        <span className={`w-2.5 h-2.5 rounded-full ${semaforoColor} flex-shrink-0`} title={semaforo ? `Semáforo: ${semaforo}` : "Sin valoración"} />
        {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>

      {expanded && (
        <div className="border-t border-slate-200 p-4 space-y-3 bg-slate-50">
          <div className="flex gap-1 border-b border-slate-200 items-center justify-between">
            <div className="flex gap-1">
              <button
                onClick={() => setSubTab("acta")}
                data-testid={`acta-subtab-acta-${acta.id}`}
                className={`px-3 py-2 text-xs border-b-2 transition ${
                  subTab === "acta" ? "border-[#0a6640] text-[#0a6640] font-medium" : "border-transparent text-slate-500"
                }`}
              >
                Acta
              </button>
              <button
                onClick={() => setSubTab("valoracion")}
                data-testid={`acta-subtab-valoracion-${acta.id}`}
                className={`px-3 py-2 text-xs border-b-2 transition ${
                  subTab === "valoracion" ? "border-[#0a6640] text-[#0a6640] font-medium" : "border-transparent text-slate-500"
                }`}
              >
                Valoración
              </button>
            </div>
            <button
              onClick={reprocesar}
              disabled={reprocesando}
              data-testid={`acta-reprocesar-${acta.id}`}
              className="text-xs text-emerald-700 hover:bg-emerald-50 px-2 py-1 rounded flex items-center gap-1 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${reprocesando ? "animate-spin" : ""}`} /> Reprocesar IA
            </button>
          </div>

          {subTab === "acta" ? (
            <>
              {acta.resumen_ia && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Resumen IA</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap bg-emerald-50 border border-emerald-100 rounded p-2">
                    {acta.resumen_ia}
                  </p>
                </div>
              )}

              {(acta.acuerdos_ia || []).length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold mb-1">Acuerdos</p>
                  <div className="flex flex-wrap gap-1.5">
                    {acta.acuerdos_ia.map((a, i) => (
                      <span key={i} className="text-xs bg-emerald-100 text-emerald-800 px-2 py-1 rounded-full">{a}</span>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <button
                  onClick={() => setShowOriginal(!showOriginal)}
                  className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-1"
                >
                  {showOriginal ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {showOriginal ? "Ocultar contenido original" : "Ver contenido original"}
                </button>
                {showOriginal && (
                  <p className="text-sm text-slate-700 whitespace-pre-wrap bg-white border border-slate-200 rounded p-2 text-xs">
                    {acta.contenido_original || "(vacío)"}
                  </p>
                )}
              </div>

              {tareasActa.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Tareas generadas ({tareasActa.length})</p>
                  <ul className="space-y-1">
                    {tareasActa.map((t) => (
                      <li key={t.id} className="flex items-center gap-2 text-sm bg-white border border-slate-200 rounded px-2 py-1">
                        <span className={`w-2 h-2 rounded-full ${t.estado === "completada" ? "bg-emerald-500" : "bg-slate-300"}`} />
                        <span className={t.estado === "completada" ? "line-through text-slate-400 flex-1" : "text-slate-700 flex-1"}>{t.texto}</span>
                        {t.urgencia && <span className={`text-[10px] px-1.5 py-0.5 rounded ${t.urgencia === 'alta' ? 'bg-rose-100 text-rose-700' : t.urgencia === 'media' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>{t.urgencia}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-1">Notas comercial</p>
                <textarea
                  value={notas}
                  onChange={(e) => setNotas(e.target.value)}
                  onBlur={notas !== (acta.notas_comercial || "") ? guardarNotas : undefined}
                  data-testid={`acta-notas-${acta.id}`}
                  rows={2}
                  className="w-full px-2 py-1.5 border border-slate-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-emerald-500 resize-y"
                  placeholder="Añade notas internas..."
                />
                {savingNotas && <p className="text-xs text-slate-400 mt-0.5">Guardando…</p>}
              </div>

              <div className="flex justify-end">
                <button
                  onClick={eliminar}
                  data-testid={`acta-eliminar-${acta.id}`}
                  className="px-3 py-1.5 text-xs text-rose-600 hover:bg-rose-50 rounded flex items-center gap-1"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Eliminar acta
                </button>
              </div>
            </>
          ) : (
            <ValoracionView val={acta.valoracion_ia} />
          )}
        </div>
      )}
    </div>
  );
}

function ValoracionView({ val }) {
  if (!val || Object.keys(val).length === 0) {
    return (
      <div className="text-sm text-slate-500 italic text-center py-6 bg-white border border-dashed border-slate-300 rounded">
        Aún no hay valoración. Espera a que la IA termine o pulsa "Reprocesar IA".
      </div>
    );
  }
  const sem = val.semaforo || "verde";
  const semDotColor = sem === "verde" ? "bg-emerald-500" : sem === "ambar" ? "bg-amber-500" : "bg-rose-500";
  const semLabel = sem === "verde" ? "Avanza bien" : sem === "ambar" ? "En riesgo" : "Alerta";
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div className={`w-3.5 h-3.5 rounded-full ${semDotColor}`} />
        <span className="font-semibold text-slate-800">{semLabel}</span>
        <span className="text-slate-500 text-sm">·</span>
        <span className="text-slate-600 text-sm">Probabilidad de cierre: <span className="font-bold text-slate-800">{val.probabilidad_cierre || 0}%</span></span>
      </div>

      {(val.riesgos || []).length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded p-3">
          <p className="text-[10px] uppercase tracking-wider text-rose-700 font-bold mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Riesgos</p>
          <ul className="text-sm text-rose-800 space-y-0.5">
            {val.riesgos.map((r, i) => <li key={i}>· {r}</li>)}
          </ul>
        </div>
      )}

      {(val.oportunidades || []).length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
          <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold mb-1 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Oportunidades</p>
          <ul className="text-sm text-emerald-800 space-y-0.5">
            {val.oportunidades.map((o, i) => <li key={i}>· {o}</li>)}
          </ul>
        </div>
      )}

      {val.recomendacion && (
        <div className="bg-blue-50 border border-blue-200 rounded p-3">
          <p className="text-[10px] uppercase tracking-wider text-blue-700 font-bold mb-1">Recomendación</p>
          <p className="text-sm text-blue-900">{val.recomendacion}</p>
        </div>
      )}
    </div>
  );
}
